import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreRowsLabels, TextDirection, UIMessage } from './types/chat.types';
import { parseInlineRefs } from './utils/parseInlineRefs';
import { parseMarkdownLinks } from './utils/parseMarkdownLinks';
import { parseMarkdownBold } from './utils/parseMarkdownBold';
import { parseMoreRows } from './utils/parseMoreRows';
import { CardListHide, hideCardLists } from './utils/parseCardLists';

interface Props {
  message: UIMessage;
  avatarEmoji?: string;
  /** Optional click handler for an inline [[ref:cart_item_id|name]] chip.
   *  Default behaviour scrolls to the matching data-cart-item-id element. */
  onRefClick?: (cartItemId: string) => void;
  /** Optional click handler for a markdown [label](url) link. Return `true`
   *  to indicate the click was handled — the default navigation is then
   *  prevented. Falsy return falls through to the normal href / Link route. */
  onLinkClick?: (url: string, e: React.MouseEvent) => boolean;
  /** The text's HTML `dir`. Defaults to 'auto'. See the comment on the text
   *  element below. */
  dir?: TextDirection;
  /** Toggle labels for a list's collapsed rows (see MoreRows). English when omitted. */
  moreRowsLabels?: MoreRowsLabels;
}

/*
 * Bidi isolation. Every inline span the bubble builds (bold, links, ref chips)
 * gets `dir="auto"`, which isolates it (`unicode-bidi: isolate`, also set in
 * chat.less) and gives it the direction of its own first strong character.
 * Without this, a Latin product name inside Hebrew text pulls the neighbouring
 * digits and punctuation into its left-to-right run: in "**Hilton** 2 לילות",
 * the "2" is shown next to "Hilton" instead of next to "לילות". W3C: wrap every
 * opposite-direction phrase tightly. In a single-direction text it changes
 * nothing.
 */

/** Innermost pass: `**bold**` → <strong>. Bold cannot contain refs or links. */
function renderTextWithBold(text: string, keyBase: string): React.ReactNode {
  return parseMarkdownBold(text).map((seg, i) =>
    seg.type === 'bold' ? (
      <strong key={`${keyBase}-b${i}`} className="bot-bubble__strong" dir="auto">
        {seg.value}
      </strong>
    ) : (
      <React.Fragment key={`${keyBase}-t${i}`}>{seg.value}</React.Fragment>
    ),
  );
}

function renderTextWithLinks(
  text: string,
  keyBase: string,
  onLinkClick?: (url: string, e: React.MouseEvent) => boolean,
): React.ReactNode {
  const segs = parseMarkdownLinks(text);
  return segs.map((seg, i) => {
    if (seg.type === 'text') {
      return (
        <React.Fragment key={`${keyBase}-t${i}`}>
          {renderTextWithBold(seg.value, `${keyBase}-t${i}`)}
        </React.Fragment>
      );
    }
    // Relative paths use react-router <Link>; absolute URLs open new tab.
    const isAbsolute = /^https?:\/\//i.test(seg.url);
    const handle = (e: React.MouseEvent) => {
      if (onLinkClick && onLinkClick(seg.url, e)) {
        e.preventDefault();
      }
    };
    if (isAbsolute) {
      return (
        <a
          key={`${keyBase}-l${i}`}
          className="bot-bubble__link"
          href={seg.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handle}
          dir="auto"
        >
          {seg.label}
        </a>
      );
    }
    return (
      <Link
        key={`${keyBase}-l${i}`}
        className="bot-bubble__link"
        to={seg.url}
        onClick={handle}
        dir="auto"
      >
        {seg.label}
      </Link>
    );
  });
}

function renderInlineRefs(
  text: string,
  onRefClick?: (id: string) => void,
  onLinkClick?: (url: string, e: React.MouseEvent) => boolean,
): React.ReactNode {
  const segments = parseInlineRefs(text);
  return segments.map((seg, i) => {
    if (seg.type === 'text') {
      return (
        <React.Fragment key={i}>
          {renderTextWithLinks(seg.value, `r${i}`, onLinkClick)}
        </React.Fragment>
      );
    }
    return (
      <button
        key={i}
        type="button"
        className="bot-bubble__ref"
        title={seg.displayName}
        dir="auto"
        onClick={() => {
          if (onRefClick) return onRefClick(seg.cartItemId);
          // Default: scroll to a card rendered with the same cart_item_id
          // (T5 will render <CartPreview> cards bearing this attribute).
          const target = document.querySelector(
            `[data-cart-item-id="${CSS.escape(seg.cartItemId)}"]`,
          );
          target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
      >
        {seg.displayName}
      </button>
    );
  });
}

/**
 * The rows of a search list that the text does not print by default — the
 * website shows more result cards than the list lists (parseMoreRows). Collapsed
 * under a toggle; expanding puts them where they belong, right under the listed
 * rows, with the toggle moving below them.
 */
const MoreRows: React.FC<{
  count: number;
  labels?: MoreRowsLabels;
  children: React.ReactNode;
}> = ({ count, labels, children }) => {
  const [open, setOpen] = useState(false);
  const show =
    count === 1
      ? labels?.showOne ?? 'Show 1 more option'
      : (labels?.showOther ?? 'Show {{count}} more options').replace('{{count}}', String(count));
  return (
    <span className="bot-bubble__more">
      {open && <span className="bot-bubble__more-rows">{children}</span>}
      <button
        type="button"
        className="bot-bubble__more-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? labels?.showLess ?? 'Show less' : show}
      </button>
    </span>
  );
};

/**
 * The marked list text (parseCardLists) this message's cards replace: every
 * marked block while the reply streams — its cards arrive only with the turn's
 * end — then the blocks of the tools whose cards came with it.
 */
function cardListHide(message: UIMessage): CardListHide {
  if (message.isStreaming) return 'all';
  return [message.searchResults?.tool, message.productDetails?.tool].filter(
    (tool): tool is string => !!tool,
  );
}

function renderAssistantText(
  message: UIMessage,
  onRefClick?: (id: string) => void,
  onLinkClick?: (url: string, e: React.MouseEvent) => boolean,
  moreRowsLabels?: MoreRowsLabels,
): React.ReactNode {
  const text = hideCardLists(message.text, cardListHide(message), !!message.isStreaming);
  return parseMoreRows(text).map((seg, i) =>
    seg.type === 'text' ? (
      <React.Fragment key={`m${i}`}>
        {renderInlineRefs(seg.value, onRefClick, onLinkClick)}
      </React.Fragment>
    ) : (
      <MoreRows key={`m${i}`} count={seg.count} labels={moreRowsLabels}>
        {renderInlineRefs(seg.value, onRefClick, onLinkClick)}
      </MoreRows>
    ),
  );
}

const MessageBubble: React.FC<Props> = ({
  message,
  avatarEmoji = '🤖',
  onRefClick,
  onLinkClick,
  dir = 'auto',
  moreRowsLabels,
}) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const showRefs = !isUser && !isSystem;

  return (
    <div className={`bot-bubble bot-bubble--${message.role}`}>
      {!isUser && !isSystem && <div className="bot-bubble__avatar">{avatarEmoji}</div>}
      <div className="bot-bubble__content">
        {/* Direction per bubble, not per widget: the chat language is detected
            from what the user types and can differ from the site language, so a
            Hebrew conversation on an LTR site would otherwise render every
            bubble left-aligned with its punctuation in the wrong place.
            The default 'auto' takes each message's first strong character,
            which also handles a mixed thread after a language switch. It gets
            one case wrong: a Hebrew reply that opens with a Latin name ("El Al
            541 …") flips the whole bubble to LTR. A host that knows the
            conversation language passes an explicit 'rtl' for those. */}
        <div className="bot-bubble__text" dir={dir}>
          {showRefs
            ? renderAssistantText(message, onRefClick, onLinkClick, moreRowsLabels)
            : message.text}
          {message.isStreaming && <span className="bot-bubble__cursor" />}
        </div>
        {!message.isStreaming && (
          <div className="bot-bubble__time">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
